import { NewsfeedApiResponseEntry } from '../notifications/api.model';
import {
  isTest,
  transformProjectData,
} from '../notifications/notification.transformers';

import {
  NavigationProjectFeedEntry,
  NavigationProjectFeedEntryApiResponse,
} from './project-feed.model';

export function stringToNumber(x?: number | string): number | undefined {
  return x ? Number(x) : undefined;
}

// This takes an API response and transforms it into a NavigationProjectFeed object
export function transformNavigationProjectFeed(
  item: NavigationProjectFeedEntryApiResponse,
): NavigationProjectFeedEntry | undefined {
  switch (item.type) {
    case 'project': {
      return {
        type: item.type,
        parent_type: 'notifications',
        id: item.id,
        data: {
          address: undefined,
          appendedDescr: item.appended_descr,
          currency: item.currency,
          currencyCode: item.currencyCode,
          exchangeRate: item.exchangerate,
          featured: item.featured,
          freeBidUntil: item.free_bid_until,
          fulltime: Boolean(item.fulltime),
          hideBids: item.hidebids,
          imgUrl: item.imgUrl,
          isLocal: false,
          isTest: false,
          jobs: item.jobs.map(jobId => Number(jobId)),
          jobString: item.jobString,
          linkUrl: item.linkUrl,
          maxBudget: Number(item.maxbudget),
          minBudget: Number(item.minbudget),
          nonpublic: item.nonpublic,
          projIsHourly: Boolean(item.projIsHourly),
          publicName: undefined,
          showBudgetMinimallyPostedProject: Boolean(
            item.showBudgetMinimallyPostedProject,
          ),
          submitDate: item.submitDate,
          text: item.text,
          time: item.time * 1000, // Turn seconds to ms
          title: item.title,
          userId: Number(item.userId),
          username: item.userName,
        },
      };
    }
    case 'contest': {
      return {
        type: item.type,
        parent_type: 'notifications',
        id: Number(item.id),
        data: {
          buyer: item.buyer,
          buyerUrl: `/u/${item.userName}`,
          currency: item.currency,
          currencyCode: item.currencyCode,
          exchangeRate: item.exchangerate,
          imgUrl: item.imgUrl,
          isTest: false,
          jobs: item.jobs.map(Number),
          jobString: item.jobString,
          linkUrl: item.linkUrl,
          // Contest min and max budgets are normally the same, as it is in this event type
          minBudget: Number(item.maxbudget),
          maxBudget: Number(item.maxbudget),
          time: item.time * 1000, // Turn seconds to ms
          title: item.title,
          userId: item.userId,
          username: item.userName,
        },
      };
    }
    default:
      return undefined;
  }
}

export function transformNavigationProjectFeedWS(
  entry: NewsfeedApiResponseEntry,
): NavigationProjectFeedEntry | undefined {
  if (isTest(entry)) {
    return undefined;
  }

  switch (entry.type) {
    case 'contest':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          buyer: entry.data.buyer,
          buyerUrl: entry.data.buyerUrl,
          currency: entry.data.currency,
          currencyCode: entry.data.currencyCode,
          exchangeRate: entry.data.exchangerate,
          imgUrl: entry.data.imgUrl,
          isTest: entry.data.isTest,
          jobs: entry.data.jobs.map(Number),
          jobString: entry.data.jobString,
          linkUrl: entry.data.linkUrl,
          maxBudget: Number(entry.data.maxbudget),
          time: entry.data.time * 1000, // Turn seconds to ms
          title: entry.data.title,
          userId: entry.data.userId,
          username: entry.data.userName,
        },
      };
    case 'failingProject': {
      const exchangerate = Number(entry.data.exchangerate);
      const minBudget = stringToNumber(entry.data.minbudget);
      const maxBudget = stringToNumber(entry.data.maxbudget);
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...transformProjectData(entry.data),
          address: entry.data.address,
          currency: entry.data.currency,
          currencyCode: entry.data.currencyCode,
          exchangeRate: entry.data.exchangerate,
          featured: entry.data.featured,
          freeBidUntil: entry.data.free_bid_until,
          fulltime: entry.data.fulltime,
          hideBids: entry.data.hideBids,
          isLocal: entry.data.isLocal,
          maxBudget,
          minBudget,
          nonpublic: entry.data.nonpublic,
          projIsHourly: entry.data.projIsHourly,
          publicName: entry.data.publicName,
          submitDate: entry.data.submitDate,
          usdMaxbudget: maxBudget
            ? Math.round(maxBudget * exchangerate)
            : undefined,
          usdMinbudget: minBudget
            ? Math.round(minBudget * exchangerate)
            : undefined,
        },
      };
    }
    case 'project': {
      const exchangerate = Number(entry.data.exchangerate);
      const minBudget = stringToNumber(entry.data.minbudget);
      const maxBudget = stringToNumber(entry.data.maxbudget);
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...transformProjectData(entry.data),
          address: entry.data.address,
          currency: entry.data.currency,
          currencyCode: entry.data.currencyCode,
          exchangeRate: entry.data.exchangerate,
          featured: entry.data.featured,
          freeBidUntil: entry.data.free_bid_until,
          fulltime: entry.data.fulltime,
          hideBids: entry.data.hideBids,
          isLocal: entry.data.isLocal,
          maxBudget,
          minBudget,
          nonpublic: entry.data.nonpublic,
          projIsHourly: entry.data.projIsHourly,
          publicName: entry.data.publicName,
          submitDate: entry.data.submitDate,
          usdMaxbudget: maxBudget
            ? Math.round(maxBudget * exchangerate)
            : undefined,
          usdMinbudget: minBudget
            ? Math.round(minBudget * exchangerate)
            : undefined,
        },
      };
    }
    case 'localJobPosted': {
      const exchangerate = Number(entry.data.exchangerate);
      const minBudget = stringToNumber(entry.data.minbudget);
      const maxBudget = stringToNumber(entry.data.maxbudget);
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...transformProjectData(entry.data),
          address: entry.data.address,
          currency: entry.data.currency,
          currencyCode: entry.data.currencyCode,
          exchangeRate: entry.data.exchangerate,
          featured: entry.data.featured,
          freeBidUntil: entry.data.free_bid_until,
          fulltime: entry.data.fulltime,
          hideBids: entry.data.hideBids,
          isLocal: entry.data.isLocal,
          maxBudget,
          minBudget,
          nonpublic: entry.data.nonpublic,
          projIsHourly: entry.data.projIsHourly,
          publicName: entry.data.publicName,
          submitDate: entry.data.submitDate,
          usdMaxbudget: maxBudget
            ? Math.round(maxBudget * exchangerate)
            : undefined,
          usdMinbudget: minBudget
            ? Math.round(minBudget * exchangerate)
            : undefined,
        },
      };
    }
    case 'xpContest':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          currency: entry.data.currency,
          currencyCode: entry.data.currencyCode,
          imgUrl: entry.data.imgUrl,
          isTest: entry.data.isTest,
          jobString: entry.data.jobString,
          linkUrl: entry.data.linkUrl,
          // Contest min and max budgets are normally the same, as it is in this event type
          minBudget: Number(entry.data.maxbudget),
          maxBudget: Number(entry.data.maxbudget),
          time: entry.data.time * 1000, // Turn seconds to ms
          title: entry.data.title,
          username: entry.data.userName,
        },
      };
    default:
      return undefined;
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/th/webapp/src/app/@shared/datastore/navigation/project-feed/project-feed.transformers.ts